import { CardContent, Separator } from '@ais-ui/ui-core';
import { RadarTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

interface RadarInfoTooltipProps {
  radarInfo: RadarTargetInfo;
}

interface RadarInfoTooltipItemProps {
  label: string;
  value: string;
}

const RadarInfoTooltipItem = ({ label, value }: RadarInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-muted-foreground flex-grow text-right">
        {value ? value : '-'}
      </p>
    </div>
  );
};

export const RadarInfoTooltip = ({ radarInfo }: RadarInfoTooltipProps) => {
  const { settings } = useSettings();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const ribbonSettings = settings['ribbonPlugin.settings'];

  // Check if any of the settings are enabled and corresponding data exists
  const hasVisibleItems =
    (ribbonSettings?.show_radar_id && radarInfo.id) ||
    (ribbonSettings?.show_radar_name && radarInfo.name) ||
    (ribbonSettings?.show_radar_sog && radarInfo.sog) ||
    (ribbonSettings?.show_radar_cog && radarInfo.cog) ||
    (ribbonSettings?.show_radar_last_report && radarInfo.lastRadarReport) ||
    (ribbonSettings?.show_radar_last_origin && radarInfo.lastOrigin) ||
    (ribbonSettings?.show_radar_latitude && radarInfo.latitude) ||
    (ribbonSettings?.show_radar_longitude && radarInfo.longitude);

  // Return null if no items will be shown
  if (!hasVisibleItems) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_radar_id && radarInfo.id && (
          <RadarInfoTooltipItem key="id" label="ID" value={radarInfo.id} />
        )}
        {ribbonSettings?.show_radar_name && radarInfo.name && (
          <RadarInfoTooltipItem
            key="name"
            label="Name"
            value={radarInfo.name}
          />
        )}
        {ribbonSettings?.show_radar_sog && radarInfo.sog && (
          <RadarInfoTooltipItem key="sog" label="SOG" value={radarInfo.sog} />
        )}
        {ribbonSettings?.show_radar_cog && radarInfo.cog && (
          <RadarInfoTooltipItem key="cog" label="COG" value={radarInfo.cog} />
        )}
        {ribbonSettings?.show_radar_last_report &&
          radarInfo.lastRadarReport && (
            <RadarInfoTooltipItem
              key="lastRadarReport"
              label="Last Report"
              value={formatDateTimeWithTimeZone(radarInfo.lastRadarReport)}
            />
          )}
        {ribbonSettings?.show_radar_last_origin && radarInfo.lastOrigin && (
          <RadarInfoTooltipItem
            key="lastOrigin"
            label="Last Origin"
            value={radarInfo.lastOrigin}
          />
        )}
        {ribbonSettings?.show_radar_latitude && radarInfo.latitude && (
          <RadarInfoTooltipItem
            key="latitude"
            label="Latitude"
            value={radarInfo.latitude.toString()}
          />
        )}
        {ribbonSettings?.show_radar_longitude && radarInfo.longitude && (
          <RadarInfoTooltipItem
            key="longitude"
            label="Longitude"
            value={radarInfo.longitude.toString()}
          />
        )}
      </div>
    </CardContent>
  );
};
